import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { PageHeader, SectionHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Couriers and zones, on one screen.
 *
 * They are one screen because they are one setup: a zone with no courier
 * delivers nothing, and a courier with no zone has nowhere defined to go.
 * Splitting them would let a manager finish half and wonder why the board is
 * empty.
 *
 * **A courier's status is not a dropdown here.** `on_delivery` is written by the
 * board and by `Location::Delivery`'s own transitions; offering it in
 * configuration would let somebody be marked available while holding a
 * customer's dinner. The one status change that belongs to a person rather than
 * to a delivery — starting and ending a shift — is the toggle below, and the
 * server refuses even that while they are carrying something.
 */

type Courier = {
  id: number
  name: string
  phone: string | null
  status: string
  statusLabel: string
  onShift: boolean
  onDelivery: boolean
}

type Zone = {
  id: number
  name: string
  radiusMeters: number
  deliveryFee: string
  minimumOrder: string
}

type Props = {
  couriers: Courier[]
  zones: Zone[]
  availableCount: number
  currency: string
}

function tCouriers(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.manage.couriers.index.${key}`, options)
}

export default function StaffLocationManageCouriersIndex({ couriers, zones, availableCount }: Props) {
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")

  return (
    <>
      <Head title={tCouriers("page_title")} />

      <MainContainer>
        <PageHeader title={tCouriers("page_title")} description={tCouriers("page_description")} />

        <Card density="compact" data-testid="courier-list-card">
          <CardHeader className="border-b">
            <CardTitle>{tCouriers("couriers_title")}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Why the board is empty, said on the screen where it gets fixed. */}
            <p
              className="text-foreground text-sm font-semibold tabular-nums"
              data-testid="courier-available-count"
            >
              {tCouriers("available_count", { count: availableCount })}
            </p>

            {couriers.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="courier-empty">
                {tCouriers("empty")}
              </p>
            ) : (
              <ul className="divide-border divide-y rounded-md border" data-testid="courier-list">
                {couriers.map((courier) => (
                  <li
                    key={courier.id}
                    className="flex flex-wrap items-center gap-3 px-4 py-3"
                    data-testid={`courier-${courier.id}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{courier.name}</span>
                      {courier.phone ? (
                        <span className="text-muted-foreground block truncate text-xs tabular-nums">
                          {courier.phone}
                        </span>
                      ) : null}
                    </span>

                    <StatusBadge
                      tone={
                        courier.onDelivery ? "occupied" : courier.onShift ? "available" : "disabled"
                      }
                      label={courier.statusLabel}
                      data-testid={`courier-status-${courier.id}`}
                    />

                    <Button
                      variant="secondary"
                      className="h-11 md:h-9"
                      disabled={courier.onDelivery}
                      data-testid={`courier-shift-${courier.id}`}
                      onClick={() =>
                        router.patch(
                          `/manage/couriers/${courier.id}`,
                          { on_shift: !courier.onShift },
                          { preserveScroll: true }
                        )
                      }
                    >
                      {courier.onShift ? tCouriers("end_shift") : tCouriers("start_shift")}
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={tCouriers("remove", { name: courier.name })}
                      data-testid={`courier-delete-${courier.id}`}
                      onClick={() =>
                        router.delete(`/manage/couriers/${courier.id}`, { preserveScroll: true })
                      }
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <form
              className="flex flex-wrap items-end gap-2"
              data-testid="courier-create"
              onSubmit={(event) => {
                event.preventDefault()
                router.post(
                  "/manage/couriers",
                  { name, phone },
                  { preserveScroll: true, onSuccess: () => { setName(""); setPhone("") } }
                )
              }}
            >
              <label className="flex min-w-40 flex-1 flex-col gap-1">
                <span className="text-muted-foreground text-xs">{tCouriers("name_label")}</span>
                <input
                  className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  data-testid="courier-name-input"
                />
              </label>

              <label className="flex w-40 flex-col gap-1">
                <span className="text-muted-foreground text-xs">{tCouriers("phone_label")}</span>
                <input
                  className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
                  inputMode="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  data-testid="courier-phone-input"
                />
              </label>

              <Button
                type="submit"
                className="h-11 md:h-9"
                disabled={name.trim() === ""}
                data-testid="courier-submit"
              >
                {tCouriers("add")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card density="compact" data-testid="zone-card">
          <CardHeader className="border-b">
            <CardTitle>{tCouriers("zones_title")}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-2">
            <SectionHeader title={tCouriers("zones_subtitle")} className="px-0" />

            {zones.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="zone-empty">
                {tCouriers("zones_empty")}
              </p>
            ) : (
              <ul className="divide-border divide-y rounded-md border" data-testid="zone-list">
                {zones.map((zone) => (
                  <li
                    key={zone.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                    data-testid={`zone-${zone.id}`}
                  >
                    <span className="truncate text-sm font-medium">{zone.name}</span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {tCouriers("zone_meta", {
                        radius: zone.radiusMeters,
                        fee: formatMoney(zone.deliveryFee),
                        minimum: formatMoney(zone.minimumOrder),
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </MainContainer>
    </>
  )
}

StaffLocationManageCouriersIndex.layout = (page: React.ReactNode) => (
  <StaffLayout>{page}</StaffLayout>
)
